import React from 'react';
import { User, Settings, HelpCircle, FileText, Coffee, LogOut, X, ChevronRight, Bookmark, List, MessageSquare, Gamepad2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { t, Language } from '../../i18n';
import { ConfirmModal } from '../Common/ConfirmModal';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

export const MobileSidebar: React.FC<MobileSidebarProps> = ({ isOpen, onClose, onNavigate }) => {
  const { user, logout } = useAuth();
  const { lang } = useLanguage();
  const [isConfirmLogoutOpen, setIsConfirmLogoutOpen] = React.useState(false);

  if (!isOpen) return null;

  const handleLinkClick = (path: string) => {
    onNavigate(path);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] md:hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-300" 
        onClick={onClose}
      />
      
      {/* Sidebar Content */}
      <div className="absolute left-0 top-0 bottom-0 w-[280px] bg-white shadow-2xl animate-in slide-in-from-left duration-300 ease-out flex flex-col">
        {/* Header: User Info */}
        <div className="p-6 border-b border-gray-50">
          <div className="flex justify-between items-start mb-6">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-slate-50 shadow-sm">
                <img 
                    src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=0f172a&color=fff`} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                />
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 md:hover:text-slate-900 transition-colors">
                <X size={20} />
            </button>
          </div>
          
          <div className="space-y-0.5">
            <h2 className="text-lg font-black text-slate-900 tracking-tight leading-tight uppercase truncate">
                {user?.name || 'Guest Explorer'}
            </h2>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest truncate">
                {user ? `@${user.email.split('@')[0]}` : t(lang, 'scoutingResultsFor')}
            </p>
          </div>

          <div className="flex gap-4 mt-4">
            <div className="flex items-baseline gap-1">
                <span className="text-sm font-black text-slate-900">0</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t(lang, 'following')}</span>
            </div>
            <div className="flex items-baseline gap-1">
                <span className="text-sm font-black text-slate-900">0</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t(lang, 'followers') || 'Followers'}</span>
            </div>
          </div>
        </div>

        {/* Primary Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-1">
            <SidebarItem 
                icon={User} 
                label={t(lang, 'mypage')} 
                onClick={() => handleLinkClick(user ? '/mypage' : '/login')} 
            />
            <SidebarItem icon={List} label={lang === 'ko' ? '리스트' : 'Lists'} onClick={() => {}} />
            {user?.role === 'admin' && (
                <SidebarItem icon={Gamepad2} label={t(lang, 'gamestore')} onClick={() => handleLinkClick('GAME_STORE')} />
            )}
            <SidebarItem icon={Bookmark} label={lang === 'ko' ? '북마크' : 'Bookmarks'} onClick={() => {}} />
            <SidebarItem icon={MessageSquare} label={t(lang, 'community')} onClick={() => handleLinkClick('/community')} />
          </nav>

          <div className="h-px bg-gray-50 my-4 mx-6" />

          <nav className="space-y-1">
            <SidebarItem icon={Settings} label={t(lang, 'settings')} onClick={() => handleLinkClick('/mypage')} />
            <SidebarItem icon={HelpCircle} label={t(lang, 'helpCenter')} onClick={() => {}} />
          </nav>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-50">
          {user ? (
            <button 
                onClick={() => setIsConfirmLogoutOpen(true)}
                className="flex items-center gap-3 text-rose-500 font-black text-xs uppercase tracking-[0.2em] w-full"
            >
                <LogOut size={16} /> {t(lang, 'logOutAccount')}
            </button>
          ) : (
            <button 
                onClick={() => handleLinkClick('/login')}
                className="bg-slate-900 text-white w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-slate-100 active:scale-95 transition-all"
            >
                {t(lang, 'signIn')}
            </button>
          )}
        </div>
      </div>

      <ConfirmModal 
          isOpen={isConfirmLogoutOpen} 
          onClose={() => setIsConfirmLogoutOpen(false)} 
          onConfirm={async () => {
              setIsConfirmLogoutOpen(false);
              await logout();
              onClose();
          }} 
          title={t(lang as Language, 'logOutAccount')} 
          description={lang === 'ko' ? '정말 로그아웃 하시겠습니까?' : 'Are you sure you want to log out?'}
          confirmLabel={t(lang as Language, 'confirm')}
          cancelLabel={t(lang as Language, 'cancel')}
      />
    </div>
  );
};

const SidebarItem = ({ icon: Icon, label, onClick }: { icon: any, label: string, onClick: () => void }) => (
    <button 
        onClick={onClick}
        className="w-full flex items-center justify-between px-6 py-3.5 md:hover:bg-gray-50 transition-colors group"
    >
        <div className="flex items-center gap-4">
            <Icon size={22} className="text-slate-900 md:group-hover:text-rose-500 transition-colors" />
            <span className="text-base font-black text-slate-900 tracking-tight uppercase">{label}</span>
        </div>
        <ChevronRight size={16} className="text-slate-200 md:group-hover:text-rose-500 transition-colors" />
    </button>
);
